import type { Channel, Socket } from "phoenix";
import type {
  ChatEvent,
  EmoteEvent,
  MoveIntent,
  NPCMovedEvent,
  PlayerJoinedEvent,
  PlayerLeftEvent,
  PlayerMovedEvent,
  ZoneSnapshot,
  ZoneJoinReply,
  MusicPlayingEvent,
  MusicStoppedEvent,
  MusicTrackChangedEvent,
} from "./types";

export interface ZoneChannelHandlers {
  onSnapshot?: (snap: ZoneSnapshot) => void;
  onPlayerJoined?: (e: PlayerJoinedEvent) => void;
  onPlayerLeft?: (e: PlayerLeftEvent) => void;
  onPlayerMoved?: (e: PlayerMovedEvent) => void;
  onChat?: (e: ChatEvent) => void;
  onEmote?: (e: EmoteEvent) => void;
  onNPCMoved?: (e: NPCMovedEvent) => void;
  onError?: (reason: unknown) => void;
  // Music — playlist + track-state events. The audio bytes themselves
  // travel over LiveKit; the channel only carries metadata.
  onMusicPlaying?: (e: MusicPlayingEvent) => void;
  onMusicStopped?: (e: MusicStoppedEvent) => void;
  onMusicTrackChanged?: (e: MusicTrackChangedEvent) => void;
}

export interface ZoneChannelHandle {
  channel: Channel;
  join: () => Promise<ZoneJoinReply>;
  leave: () => Promise<void>;
  sendMove: (intent: MoveIntent) => void;
  sendChat: (text: string) => void;
  sendEmote: (emoteId: string) => void;
  // Music
  sendMusicPlaying: (trackName: string, playlist: string[]) => void;
  sendMusicStopped: () => void;
  sendMusicTrackChanged: (trackName: string, index: number) => void;
}

export function joinZone(
  socket: Socket,
  zoneId: string,
  instance: string,
  handlers: ZoneChannelHandlers,
): ZoneChannelHandle {
  const topic = `zone:${zoneId}:${instance}`;
  const channel = socket.channel(topic, {});

  if (handlers.onSnapshot) channel.on("state_snapshot", handlers.onSnapshot);
  if (handlers.onPlayerJoined) channel.on("player_joined", handlers.onPlayerJoined);
  if (handlers.onPlayerLeft) channel.on("player_left", handlers.onPlayerLeft);
  if (handlers.onPlayerMoved) channel.on("player_moved", handlers.onPlayerMoved);
  if (handlers.onChat) channel.on("chat", handlers.onChat);
  if (handlers.onEmote) channel.on("emote", handlers.onEmote);
  if (handlers.onNPCMoved) channel.on("npc_moved", handlers.onNPCMoved);
  if (handlers.onMusicPlaying) channel.on("music:playing", handlers.onMusicPlaying);
  if (handlers.onMusicStopped) channel.on("music:stopped", handlers.onMusicStopped);
  if (handlers.onMusicTrackChanged) channel.on("music:track_changed", handlers.onMusicTrackChanged);

  return {
    channel,
    join: () =>
      new Promise((resolve, reject) => {
        channel
          .join()
          .receive("ok", (resp: ZoneJoinReply) => resolve(resp))
          .receive("error", (resp) => {
            handlers.onError?.(resp);
            reject(resp);
          })
          .receive("timeout", () => reject(new Error("join_timeout")));
      }),
    leave: () =>
      new Promise((resolve) => {
        channel.leave().receive("ok", () => resolve());
      }),
    sendMove: (intent) => {
      channel.push("move", intent);
    },
    sendChat: (text) => {
      channel.push("chat", { text });
    },
    sendEmote: (emoteId) => {
      channel.push("emote", { emote_id: emoteId });
    },
    sendMusicPlaying: (trackName, playlist) => {
      channel.push("music:playing", { track_name: trackName, playlist });
    },
    sendMusicStopped: () => {
      channel.push("music:stopped", {});
    },
    sendMusicTrackChanged: (trackName, index) => {
      channel.push("music:track_changed", { track_name: trackName, index });
    },
  };
}
